import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ReactJson from 'react-json-view';
import { push } from 'connected-react-router';
import PropTypes from 'prop-types';
import {
  Button,
  Header,
  Icon,
  Menu,
} from 'semantic-ui-react';

import { errorType, recognitionType } from '../types';

import RefreshButton from '../utils/refresh-button';

import { Processing } from './recognitionsTable';
import {
  durationTimeString,
  formatHoursMinutesSeconds,
} from '../../modules/utils';
import {
  clearError,
  getRecognition,
  getRemeetingRecognition,
  deleteRecognition,
} from '../../modules/recognition';

const RECOGNITION_UPDATE_INTERVAL_MS = 3000;
const RANDOM_RANGE = 10000;

class Recognition extends Component {
  componentDidMount() {
    const { connectedClearError } = this.props;
    this.updateRecognition();
    connectedClearError();
  }

  componentWillUnmount() {
    const { connectedClearError } = this.props;
    connectedClearError();
  }

  deleteRecognition = (event, id) => {
    const { connectedDeleteRecognition, conectedPush } = this.props;
    event.preventDefault();
    connectedDeleteRecognition(id).then(() => { conectedPush('/recognitions'); });
  }

  updateRecognition = () => {
    const {
      match,
      recognition,
      connectedGetRecognition,
      connectedGetRemeetingRecognition,
    } = this.props;
    const { recognitionId } = match.params;

    function completed(status) {
      return status === 'completed' || status === 'failed' || status === undefined;
    }

    if (recognition.id === recognitionId && completed(recognition.status)) {
      return;
    }

    connectedGetRecognition(recognitionId);
    connectedGetRemeetingRecognition(recognitionId);
  }

  /* TODO: render loading symbol while waiting to get recognition data. */

  /* Page header
   *
   * Creates the title and primary actions, which are
   * currently limited to deleting the recognition and
   * viewing the parameters for replicating the
   * Recognition.POST request
   * */
  renderHeader = () => {
    const {
      match,
      recognition,
    } = this.props;
    const { recognitionId } = match.params;

    return (
      <div className="re is-header">
        <Menu secondary borderless className="re is-header-row is-alignItems-center">
          <Header as="h1" className="re is-header-item">
            <Link to="/recognitions">
              <Icon name="chevron left" />
            </Link>
            { `Recognition: ${recognitionId}` }
          </Header>
          <Menu.Menu position="right">
            <RefreshButton
              member={recognition}
              callback={this.updateRecognition}
              delay={RECOGNITION_UPDATE_INTERVAL_MS}
              filter={Processing}
            />
            <Button
              disabled={Processing(recognition)}
              negative
              onClick={e => this.deleteRecognition(e, recognition.id)}
              content="Delete"
            />
          </Menu.Menu>
        </Menu>
      </div>
    );
  }

  /* Left pane content
   *
   * Parses the Recognition.GET response to display the transcript
   */
  renderTranscript = (recognition) => {
    if (!recognition) { return null; }

    function generateSegmentsMap() {
      return recognition.segments.map(result => (
        <div key={Math.random() * RANDOM_RANGE} className="ui segment">
          <p className="re is-margin-none">
            {result.transcript_formatted}
          </p>
          <p className="re is-textAlign-right is-color-black-70">
            { `${recognition.speakers[result.speaker]} | ${formatHoursMinutesSeconds(result.interval[0])}` }
          </p>
        </div>
      ));
    }

    if (recognition.segments) {
      return generateSegmentsMap(recognition);
    }
    return null;
  }

  renderDuration = (recognition) => {
    if (!recognition || !recognition.duration) { return null; }

    return (
      <p className="re is-marginLeft-auto">
        Duration:
        { ` ${durationTimeString(recognition)}` }
      </p>
    );
  }

  /* Right pane
   *
   * Display the parameters of the Recognition.GET request
   * and the response body in a JSON explorer. Also provides
   * the ability to adjust the format output of the request
   */
  renderGetRequestAndResponse = (getRecognitionRequestError, recognition) => {
    const body = (getRecognitionRequestError && getRecognitionRequestError.error) || recognition;
    return (
      <div className="re is-flexGrow-1 recognition-get-section">
        <Header as="h3">
            Response Body
        </Header>
        <ReactJson src={body} collapsed={1} name={false} />
      </div>
    );
  }

  /* Primary render function */
  render() {
    const {
      getRecognitionRequestError, recognition, remeetingRecognition,
    } = this.props;
    return (
      <div className="re is-main is-background-black-3 is-full-height is-flex is-flexDirection-column">
        { this.renderHeader(recognition) }
        <div className="re is-full-height is-flex is-flexGrow-1">
          <div className="recognition-transcript-section">
            <div className="re is-flex is-alignItems-center">
              <h2 className="re is-margin-none">
                Transcript
              </h2>
              { this.renderDuration(remeetingRecognition) }
            </div>
            { this.renderTranscript(remeetingRecognition) }
          </div>
          { this.renderGetRequestAndResponse(getRecognitionRequestError, recognition) }
        </div>
      </div>
    );
  }
}

Recognition.propTypes = {
  recognition: recognitionType.isRequired,
  remeetingRecognition: recognitionType.isRequired,
  connectedClearError: PropTypes.func.isRequired,
  connectedDeleteRecognition: PropTypes.func.isRequired,
  connectedGetRecognition: PropTypes.func.isRequired,
  connectedGetRemeetingRecognition: PropTypes.func.isRequired,
  getRecognitionRequestError: errorType,
  match: PropTypes.shape({
    params: PropTypes.shape({
      recognitionId: PropTypes.node,
    }).isRequired,
  }).isRequired,
  conectedPush: PropTypes.func.isRequired,
};

Recognition.defaultProps = {
  getRecognitionRequestError: undefined,
};

// TODO: Add getRecognitionRequestError handling
const mapStateToProps = state => ({
  recognition: state.recognition.recognition,
  remeetingRecognition: state.recognition.remeetingRecognition,
  getRecognitionRequestError: state.recognition.getRecognitionRequestError,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  connectedClearError: clearError,
  connectedDeleteRecognition: deleteRecognition,
  connectedGetRecognition: getRecognition,
  connectedGetRemeetingRecognition: getRemeetingRecognition,
  conectedPush: push,
}, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Recognition);
